import React, { useMemo, useState, useCallback } from 'react';
import { MarketingContent } from '../../types/marketing';
import { WorkflowTemplate } from '../../utils/marketingTemplates';
import MarketingCard from './MarketingCard';
import './MarketingKanban.css';

interface MarketingKanbanProps {
  items: MarketingContent[];
  template: WorkflowTemplate;
  onSelect: (item: MarketingContent) => void;
  onDropToStage: (item: MarketingContent, targetStageId: string) => void | Promise<void>;
}

const MarketingKanban: React.FC<MarketingKanbanProps> = ({ items, template, onSelect, onDropToStage }) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [hoverStageId, setHoverStageId] = useState<string | null>(null);

  const columns = useMemo(
    () =>
      template.stages.map(stage => ({
        ...stage,
        items: items.filter(i => i.currentStageId === stage.id),
      })),
    [items, template]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, stageId: string) => {
      e.preventDefault();
      setHoverStageId(null);
      if (!draggedId) return;
      const item = items.find(i => i.id === draggedId);
      if (!item || item.currentStageId === stageId) {
        setDraggedId(null);
        return;
      }
      void onDropToStage(item, stageId);
      setDraggedId(null);
    },
    [draggedId, items, onDropToStage]
  );

  return (
    <div className="mh-kanban pm-scroll">
      {columns.map(col => (
        <div
          key={col.id}
          className={`mh-kanban-col ${hoverStageId === col.id ? 'drop-hover' : ''}`}
          onDragOver={e => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (hoverStageId !== col.id) setHoverStageId(col.id);
          }}
          onDragLeave={() => {
            if (hoverStageId === col.id) setHoverStageId(null);
          }}
          onDrop={e => handleDrop(e, col.id)}
        >
          <div className="mh-kanban-col-header">
            <span className="mh-kanban-col-dot" style={{ background: col.color }} />
            <span className="mh-kanban-col-label">{col.label}</span>
            <span className="mh-kanban-col-count">{col.items.length}</span>
          </div>
          <div className="mh-kanban-col-body pm-scroll">
            {col.items.length === 0 && (
              <div className="mh-kanban-col-empty">Drag here</div>
            )}
            {col.items.map((item, i) => (
              <div
                key={item.id}
                draggable
                onDragStart={() => setDraggedId(item.id)}
                onDragEnd={() => {
                  setDraggedId(null);
                  setHoverStageId(null);
                }}
              >
                <MarketingCard
                  item={item}
                  template={template}
                  onClick={() => onSelect(item)}
                  animDelay={i * 40}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MarketingKanban;
